import { RiskScore } from "@tfg/db"
import { CountrySelectorCustomRender, FilterOptions } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { useHeaderControlsContext } from "~/components/contexts/useHeaderControlsContext"
import {
  businessRelationshipDtoTypeEnum,
  FindContactRequestFiltersDto,
  findProductionSitesByBusinessPartners,
  findTags,
  useFindProductionSitesByBusinessPartners,
  useFindTags,
} from "~/gen/api"
import { COUNTRIES } from "~/utils/countries"

export const useBuildBusinessPartnerFilters = () => {
  const { t } = useTranslation(["traceability", "suppliers"])
  const { filters, setFilters } = useHeaderControlsContext()
  const { data: tagsData, isFetching: isTagsLoading } = useFindTags()
  const { data: productionSitesData, isFetching: isProductionSitesLoading } =
    useFindProductionSitesByBusinessPartners()

  const filterOptions: FilterOptions<FindContactRequestFiltersDto>[] = [
    {
      label: t("supplierSettings.filters.status"),
      key: "active",
      type: "select",
      options: [
        { label: t("contactTable.status.active"), value: true },
        { label: t("contactTable.status.inactive"), value: false },
      ],
    },
    {
      label: t("contactTable.columns.type.name"),
      key: "type",
      type: "select",
      options: [
        {
          label: t("contactModal.labels.companyRelationshipType.AGENT"),
          value: businessRelationshipDtoTypeEnum.AGENT,
        },
        {
          label: t("contactModal.labels.companyRelationshipType.MANUFACTURER"),
          value: businessRelationshipDtoTypeEnum.MANUFACTURER,
        },
        {
          label: t("contactModal.labels.companyRelationshipType.SUPPLIER"),
          value: businessRelationshipDtoTypeEnum.SUPPLIER,
        },
        {
          label: t(
            "contactModal.labels.companyRelationshipType.RAW_MATERIAL_SUPPLIER"
          ),
          value: businessRelationshipDtoTypeEnum.RAW_MATERIAL_SUPPLIER,
        },
        {
          label: t("contactModal.labels.companyRelationshipType.TRADER"),
          value: businessRelationshipDtoTypeEnum.TRADER,
        },
        {
          label: t("contactModal.labels.companyRelationshipType.BRAND"),
          value: businessRelationshipDtoTypeEnum.BRAND,
        },
        {
          label: t("contactModal.labels.companyRelationshipType.OTHER"),
          value: businessRelationshipDtoTypeEnum.OTHER,
        },
      ],
    },
    {
      label: t("contactTable.columns.location.name"),
      key: "country",
      type: "select",
      options: Object.entries(COUNTRIES).map(([_, value]) => ({
        label: value.countryName,
        value: value.code as FindContactRequestFiltersDto["country"],
      })),
      customRender: CountrySelectorCustomRender(COUNTRIES),
    },
    {
      label: t("supplierSettings.filters.tags"),
      key: "tagNames",
      type: "async-multi-select",
      options:
        tagsData?.data?.tags.map((tag: { name: string }) => ({
          label: tag.name,
          value: tag.name,
        })) ?? [],
      asyncRefetch: async (searchQuery?: string) => {
        const result = await findTags()
        const tags = result.data?.tags ?? []

        if (searchQuery) {
          const query = searchQuery.toLowerCase()
          return tags
            .filter((tag) => tag.name.toLowerCase().includes(query))
            .map((tag) => ({
              label: tag.name,
              value: tag.name,
            }))
        }

        return tags.map((tag) => ({
          label: tag.name,
          value: tag.name,
        }))
      },
      isAsyncLoading: isTagsLoading,
    },
    {
      label: t("supplierSettings.filters.productionSite"),
      key: "productionSiteIds",
      type: "async-multi-select",
      options:
        productionSitesData?.data?.map((site) => ({
          label: site.name ?? "Unknown",
          value: site.id ?? "",
        })) ?? [],
      asyncRefetch: async (searchQuery?: string) => {
        const result = await findProductionSitesByBusinessPartners()
        const sites = result.data ?? []

        if (searchQuery) {
          const query = searchQuery.toLowerCase()
          return sites
            .filter((site) => site.name?.toLowerCase().includes(query) ?? false)
            .map((site) => ({
              label: site.name ?? "Unknown",
              value: site.id ?? "",
            }))
        }

        return sites.map((site) => ({
          label: site.name ?? "Unknown",
          value: site.id ?? "",
        }))
      },
      isAsyncLoading: isProductionSitesLoading,
    },
    {
      label: t("contactTable.columns.activity.name"),
      key: "lastLogin",
      type: "dateRange",
      options: [],
    },
    {
      label: t("contactTable.columns.reminder.name"),
      key: "lastReminderDate",
      type: "dateRange",
      options: [],
    },
    {
      label: t("suppliers:risk.table.collaborationRisk.title"),
      key: "collaborationRisk",
      type: "select",
      options: [
        {
          label: t("suppliers:risk.low"),
          value: RiskScore.LOW,
        },
        {
          label: t("suppliers:risk.medium"),
          value: RiskScore.MEDIUM,
        },
        {
          label: t("suppliers:risk.high"),
          value: RiskScore.HIGH,
        },
      ],
    },
    {
      label: t("suppliers:risk.table.certificationRisk.title"),
      key: "certificateRisk",
      type: "select",
      options: [
        {
          label: t("suppliers:risk.low"),
          value: RiskScore.LOW,
        },
        {
          label: t("suppliers:risk.medium"),
          value: RiskScore.MEDIUM,
        },
        {
          label: t("suppliers:risk.high"),
          value: RiskScore.HIGH,
        },
      ],
    },
  ]

  return {
    filters,
    setFilters,
    filterOptions,
  }
}
